import React, { useCallback, useEffect, useMemo, useRef, useState } from 'react';
import { api, mutate } from '../shared/api';
import { HedgeTradesPanel } from './hedgeTradesPanel';
import { decimalInput, formatMoney, formatNumber, localIsoDate } from '../shared/formatting';
import { BrandSpinner } from '../shared/BrandSpinner';
import { Breadcrumb } from '../shared/Breadcrumb';
import { CoverageDayView } from './coverageDayView';
import { UnknownWarehousesPopup, WarehouseClassifySettings } from './coverageWarehouseClassify';

type Locale = 'hr' | 'en';

type Signal = {
    band_pct: number;
    deviation_pct: number | null;
    status: 'green' | 'red' | 'unknown';
    direction: 'shortage' | 'surplus' | 'flat';
    avg_daily_order_eur: number;
    window_days: number;
    days: number | null;
};

type Row = {
    key: string;
    label: string;
    group: string;
    in_z1: boolean;
    qty: number;
    fine_g: number;
    book_eur: number;
    spot_eur: number;
    note: string | null;
    signal?: Signal;
    order_count?: number;
    remaining_eur?: number;
    savings?: { qty: number; book_eur: number; order_count: number };
    packaging?: { qty: number; book_eur: number; order_count: number };
    all_transit?: { qty: number; fine_g: number; book_eur: number; spot_eur: number };
};

type HistoryPoint = { date: string; qty: number; book_eur: number; fine_g: number; spot_eur: number; definition_version?: string | null };

/** Datumi na kojima se verzija definicije snimke promijenila (audit 03.10. T11). */
function definitionBreaks(points: HistoryPoint[]): string[] {
    const out: string[] = [];
    for (let i = 1; i < points.length; i++) {
        if ((points[i].definition_version ?? null) !== (points[i - 1].definition_version ?? null)) out.push(points[i].date);
    }
    return out;
}

type WarehouseCheck = {
    warehouse_code: string;
    warehouse_name: string;
    physical_qty: number;
    s2_qty: number;
    allocated_qty: number;
    l1_stanje_qty: number;
};

/** Metrike za koje backend daje razradu (CoverageService::DETAIL_METRICS). */
const DETAIL_METRICS = ['s1', 's2', 's2b', 's3', 's4', 't1', 'bis_s3', 'bis_t1', 'l2_poslovnice', 'l3_skladiste', 'l_na_putu', 'l2_stanje', 'i1'];

/**
 * V3 (mail 861): odjeljci po V2 zahtjevu i zemlja kojoj pripadaju. Sve = bez filtra.
 * Za SI i BG BIS ima samo rucne unose i Faros skladista razvrstana u kantu.
 */
type Country = 'all' | 'HR' | 'SI' | 'BG';
const SECTIONS: Array<{ key: string; countries: Country[] }> = [
    { key: 'coverage', countries: ['HR'] },
    { key: 'lager', countries: ['HR'] },
    { key: 'akcijsko', countries: ['HR'] },
    { key: 'komisija', countries: ['HR'] },
    { key: 'gg_slo', countries: ['SI'] },
    { key: 'gtm', countries: ['BG'] },
    { key: 'result', countries: ['HR', 'SI', 'BG'] },
    { key: 'zajam', countries: ['HR'] },
    { key: 'control', countries: ['HR'] },
];
/** U odjeljku Razlika SI i BG vide samo svoj H redak. */
const RESULT_BY_COUNTRY: Partial<Record<Country, string[]>> = { SI: ['stock_si'], BG: ['stock_bg'] };
const TOTAL_ROWS = ['z1', 'z1b', 'z1_v2', 'l_stanje', 'l_plan_vs_stanje', 'komisija_ukupno'];
/** H1 nema backend razradu nego popis naloga iz Hedginga (N7). */
const HEDGE_METRICS = ['h1'];

type DetailArticle = {
    sku: string;
    name: string | null;
    metal: 'Au' | 'Ag';
    qty: number;
    fine_g: number;
    book_eur: number;
    spot_eur: number | null;
    physical_qty?: number;
};

type DetailWarehouse = {
    warehouse_code: string;
    warehouse_name: string;
    qty: number;
    fine_g: number;
    book_eur: number;
    spot_eur: number;
    delivery_only?: boolean;
    physical_qty?: number;
    au_fine_g?: number;
    ag_qty?: number;
    ag_fine_g?: number;
    articles?: DetailArticle[];
    silver_articles?: DetailArticle[];
};

type DetailPart = { key?: string; label: string; qty: number; fine_g: number; book_eur: number; spot_eur?: number; set_at?: string | null; set_by?: string | null };

type Detail = {
    metric: string;
    as_of: string;
    eur_per_gram: number;
    totals: { qty: number; fine_g: number; book_eur: number; spot_eur: number };
    warehouses: DetailWarehouse[];
    articles: DetailArticle[];
    silver_articles: DetailArticle[];
    parts: DetailPart[];
    by_source: Array<{ label: string; qty: number }>;
    by_layer: Array<{ label: string; qty: number }>;
    info: {
        gross?: { qty: number; fine_g: number; book_eur: number; spot_eur: number };
        writeoff_fine_g?: number;
        writeoff_spot_eur?: number;
        order_count?: number;
        remaining_eur?: number;
        manual_unattributed_book_eur?: number;
        manual_unattributed_fine_g?: number;
        rows_priced_by_spot?: number;
        silver?: { qty: number; fine_g: number; book_eur: number };
        savings?: { qty: number; book_eur: number; order_count: number };
        packaging?: { qty: number; book_eur: number; order_count: number };
        items_book_eur?: number;
        header_minus_items_eur?: number;
    };
};

type Overview = {
    as_of: string;
    spot: { eur_per_gram: number; eur_per_troy_oz: number; spot_date: string | null };
    freshness: {
        stock_batch_id: number | null;
        stock_as_of: string | null;
        stock_age_days: number | null;
        open_orders_as_of: string | null;
        unknown_warehouses?: { code: string; name: string; rows: number }[];
        open_orders_age_hours?: number | null;
        open_orders_stale?: boolean;
        stale: boolean;
    };
    rows: Row[];
    signal: Signal;
    transit_reconcile: {
        snapshot_date: string | null;
        faros_qty_in_transit: number | null;
        bis_outbound_qty: number;
        bis_inbound_qty: number;
        bis_total_qty: number;
        difference: number | null;
    };
    double_count_check: {
        by_warehouse: WarehouseCheck[];
        overflow: Array<{ warehouse_code: string; item_code: string; stock_qty: number; reserved_qty: number; committed_qty: number }>;
        ok: boolean;
    };
    settings: {
        band_pct: number; window_days: number; h1_manual_eur: number; h1_manual_kg?: number;
        loris_manual_eur?: number; loris_manual_fine_g?: number;
        stock_si_manual_eur?: number; stock_si_manual_fine_g?: number;
        stock_bg_manual_eur?: number; stock_bg_manual_fine_g?: number;
        gg_slo_open_eur?: number; gg_slo_open_fine_g?: number;
        gtm_open_eur?: number; gtm_open_fine_g?: number;
    };
    can_edit: boolean;
};

const T = {
    hr: {
        title: 'Pokrivenost zlata V3',
        lead: 'Pokrivenost narudzbi S1-S5, lager L1-L5, akcijsko, komisija, GG SLO, GTM i razlika H1-H4. Z1 sa semaforom, Z1b bez H1-H4, Z1-V2 po formuli V2.',
        coverage: 'Pokrivenost narudzbi',
        lager: 'Lager (izvan formule Z1)',
        result: 'Razlika',
        sections: {
            coverage: 'Pokrivenost narudzbi',
            lager: 'Lager (izvan formule Z1)',
            akcijsko: 'Roba dostupna odmah - akcijsko',
            komisija: 'Komisija',
            gg_slo: 'GG SLO',
            gtm: 'GTM',
            result: 'Razlika',
            zajam: 'ADO zajam - evidencijski',
            control: 'Kontrole (izvan pokrivenosti)',
        } as Record<string, string>,
        country: 'Zemlja',
        countries: { all: 'Sve', HR: 'Hrvatska', SI: 'Slovenija', BG: 'Bugarska' } as Record<string, string>,
        countryNote: 'Za Sloveniju i Bugarsku BIS ima rucne unose i Faros skladista razvrstana u odjeljak; ostalo je hrvatsko poslovanje.',
        ggSloOpen: 'GG SLO otvorene narudzbe rucno (EUR)',
        ggSloOpenFine: 'GG SLO otvorene narudzbe rucno (fino g)',
        gtmOpen: 'GTM otvorene narudzbe rucno (EUR)',
        gtmOpenFine: 'GTM otvorene narudzbe rucno (fino g)',
        book: 'Knjigovodstveno EUR',
        fine: 'Fine XAU g',
        spot: 'Spot EUR',
        qty: 'Kom',
        item: 'Stavka',
        stale: 'Zaliha nije od danas ili je snimka otvorenih narudzbi starija od 24 h. Brojke kasne.',
        asOf: 'Faros zaliha',
        openOrders: 'Narudzbe',
        spotLabel: 'Spot',
        green: 'Unutar praga',
        red: 'Izvan praga',
        unknown: 'Nema baze za postotak',
        shortage: 'manjak',
        surplus: 'visak',
        flat: 'na nuli',
        days: 'dana prometa',
        settings: 'Postavke',
        band: 'Prag semafora (% od I1)',
        window: 'Prozor za prosjek narudzbi (dana)',
        h1: 'H1 rucno (EUR)',
        h1Kg: 'H1 rucno (kg, ima prednost pred EUR)',
        loris: 'H2 Loris / ADO zajam rucno (EUR)',
        lorisFine: 'H2 Loris / ADO zajam rucno (fino g)',
        stockSi: 'H3 Slovenija rucno (EUR)',
        stockSiFine: 'H3 Slovenija rucno (fino g)',
        stockBg: 'H4 Bugarska rucno (EUR)',
        stockBgFine: 'H4 Bugarska rucno (fino g)',
        save: 'Spremi',
        setSignal: 'Semafor',
        setManual: 'Rucni unos (dok nema automatskog izvora)',
        colItem: 'Stavka',
        colEur: 'EUR',
        colQty: 'Kolicina',
        unitG: 'fino g',
        unitKg: 'kg (ima prednost pred EUR)',
        rowH1: 'H1 Hrvatska - StoneX pokrice',
        rowH2: 'H2 Loris / ADO zajam',
        rowH3: 'H3 Slovenija',
        rowH4: 'H4 Bugarska',
        rowGgSlo: 'GG SLO otvorene narudzbe',
        rowGtm: 'GTM otvorene narudzbe',
        saved: 'Spremljeno.',
        reconcile: 'Sravnjenje robe na putu s Farosom',
        farosTotal: 'Faros ukupno na putu',
        bisOut: 'BIS S3 prema poslovnicama',
        bisIn: 'BIS povrat prema veleprodaji',
        diff: 'Razlika',
        check: 'Kontrola dvostrukog brojanja',
        checkOk: 'Nijedno skladiste ne broji istu robu dvaput.',
        checkBad: 'Rezervacije i alokacije prelaze fizicko stanje na ovim celijama:',
        physical: 'Fizicki',
        reserved: 'S2',
        allocated: 'Alocirano',
        free: 'Lager (L2/L3)',
        trend: 'Povijest',
        trendClose: 'Zatvori',
        trend30: '30 d',
        trend365: '365 d',
        trendEmpty: 'Nema jos snimljenih dana za ovu stavku.',
        trendLoading: 'Ucitavam...',
        view: 'Prikazi',
        close: 'Zatvori',
        detailTitle: 'Razrada',
        warehouse: 'Skladiste',
        sku: 'SKU',
        articleName: 'Naziv artikla',
        auQty: 'Au kom',
        auFine: 'Au fine g',
        agQty: 'Ag kom',
        agFine: 'Ag fine g',
        deliveryOnly: 'samo isporuke',
        silverApart: 'Srebro (Ag), izvan zbroja zlata',
        noArticles: 'Nema artikala.',
        noDetail: 'Nema podataka za razradu.',
        parts: 'Po izvoru',
        bySource: 'Po izvoru',
        byLayer: 'Po sloju',
        gross: 'Bruto',
        writeoffs: 'repovi',
        orders: 'Broj narudzbi',
        unpaid: 'Nenaplaceno',
        unattributed: 'Rucni zbirni unos bez artikla',
        pricedBySpot: 'Redaka vrednovanih po spotu',
        expand: 'Artikli',
    },
    en: {
        title: 'Gold coverage V3',
        lead: 'Order coverage S1-S5, lager L1-L5, promo, consignment, GG SLO, GTM and gap H1-H4. Z1 with traffic light, Z1b without H1-H4, Z1-V2 per the V2 formula.',
        coverage: 'Order coverage',
        lager: 'Lager (outside Z1)',
        result: 'Gap',
        sections: {
            coverage: 'Order coverage',
            lager: 'Lager (outside Z1)',
            akcijsko: 'Available now - promo',
            komisija: 'Consignment',
            gg_slo: 'GG SLO',
            gtm: 'GTM',
            result: 'Gap',
            zajam: 'ADO loan - record only',
            control: 'Controls (outside coverage)',
        } as Record<string, string>,
        country: 'Country',
        countries: { all: 'All', HR: 'Croatia', SI: 'Slovenia', BG: 'Bulgaria' } as Record<string, string>,
        countryNote: 'For Slovenia and Bulgaria BIS has manual entries and Faros warehouses assigned to the section; the rest is Croatian business.',
        ggSloOpen: 'GG SLO open orders manual (EUR)',
        ggSloOpenFine: 'GG SLO open orders manual (fine g)',
        gtmOpen: 'GTM open orders manual (EUR)',
        gtmOpenFine: 'GTM open orders manual (fine g)',
        book: 'Book EUR',
        fine: 'Fine XAU g',
        spot: 'Spot EUR',
        qty: 'Pcs',
        item: 'Item',
        stale: 'Stock is not from today or the open orders snapshot is older than 24 h. Figures are late.',
        asOf: 'Faros stock',
        openOrders: 'Orders',
        spotLabel: 'Spot',
        green: 'Within band',
        red: 'Outside band',
        unknown: 'No base for percentage',
        shortage: 'shortage',
        surplus: 'surplus',
        flat: 'at zero',
        days: 'days of orders',
        settings: 'Settings',
        band: 'Traffic light band (% of I1)',
        window: 'Order average window (days)',
        h1: 'H1 manual (EUR)',
        h1Kg: 'H1 manual (kg, takes precedence over EUR)',
        loris: 'H2 Loris / ADO loan manual (EUR)',
        lorisFine: 'H2 Loris / ADO loan manual (fine g)',
        stockSi: 'H3 Slovenia manual (EUR)',
        stockSiFine: 'H3 Slovenia manual (fine g)',
        stockBg: 'H4 Bulgaria manual (EUR)',
        stockBgFine: 'H4 Bulgaria manual (fine g)',
        save: 'Save',
        setSignal: 'Signal',
        setManual: 'Manual input (until an automatic source exists)',
        colItem: 'Item',
        colEur: 'EUR',
        colQty: 'Quantity',
        unitG: 'fine g',
        unitKg: 'kg (takes precedence over EUR)',
        rowH1: 'H1 Croatia - StoneX hedge',
        rowH2: 'H2 Loris / ADO loan',
        rowH3: 'H3 Slovenia',
        rowH4: 'H4 Bulgaria',
        rowGgSlo: 'GG SLO open orders',
        rowGtm: 'GTM open orders',
        saved: 'Saved.',
        reconcile: 'Goods in transit reconciled with Faros',
        farosTotal: 'Faros total in transit',
        bisOut: 'BIS S3 to branches',
        bisIn: 'BIS return to wholesale',
        diff: 'Difference',
        check: 'Double counting check',
        checkOk: 'No warehouse counts the same goods twice.',
        checkBad: 'Reservations and allocations exceed physical stock on these cells:',
        physical: 'Physical',
        reserved: 'S2',
        allocated: 'Allocated',
        free: 'Lager (L2/L3)',
        trend: 'History',
        trendClose: 'Close',
        trend30: '30 d',
        trend365: '365 d',
        trendEmpty: 'No snapshot days recorded yet for this row.',
        trendLoading: 'Loading...',
        view: 'View',
        close: 'Close',
        detailTitle: 'Breakdown',
        warehouse: 'Warehouse',
        sku: 'SKU',
        articleName: 'Item name',
        auQty: 'Au pcs',
        auFine: 'Au fine g',
        agQty: 'Ag pcs',
        agFine: 'Ag fine g',
        deliveryOnly: 'deliveries only',
        silverApart: 'Silver (Ag), outside gold totals',
        noArticles: 'No items.',
        noDetail: 'No breakdown data.',
        parts: 'By source',
        bySource: 'By source',
        byLayer: 'By layer',
        gross: 'Gross',
        writeoffs: 'write-offs',
        orders: 'Order count',
        unpaid: 'Unpaid',
        unattributed: 'Manual lump sum without item',
        pricedBySpot: 'Rows priced by spot',
        expand: 'Items',
    },
} as const;

/** Crno-bijela mini crta bez ovisnosti — jedna vrijednost (book_eur) kroz vrijeme. */
function Sparkline({ points }: { points: HistoryPoint[] }) {
    if (points.length < 2) return null;
    const w = 240;
    const h = 48;
    const pad = 4;
    const values = points.map((p) => p.book_eur);
    const min = Math.min(...values);
    const max = Math.max(...values);
    const span = max - min || 1;
    const stepX = (w - pad * 2) / (points.length - 1);
    const coords = values.map((v, i) => {
        const x = pad + i * stepX;
        const y = pad + (h - pad * 2) * (1 - (v - min) / span);
        return `${x.toFixed(1)},${y.toFixed(1)}`;
    });
    const zeroY = pad + (h - pad * 2) * (1 - (0 - min) / span);
    const showZero = min < 0 && max > 0;
    return (
        <svg width={w} height={h} className="coverage-sparkline" role="img" aria-hidden="true">
            {showZero ? (
                <line x1={pad} y1={zeroY} x2={w - pad} y2={zeroY} stroke="#999" strokeDasharray="2,2" />
            ) : null}
            <polyline points={coords.join(' ')} fill="none" stroke="#111" strokeWidth={1.5} />
        </svg>
    );
}

/**
 * 40.63 Pokrivenost zlata V3 (mail 861, V2 zahtjev Ruzice 07.10.2026, odgovori Vedrana
 * 12.10.2026). Zaseban modul uz 40.61, koji ostaje kakav jest.
 */
export function CoverageV3Module({
    token,
    locale,
    onBack,
}: {
    token: string;
    locale: Locale;
    onBack?: () => void;
}) {
    const t = T[locale];
    const [data, setData] = useState<Overview | null>(null);
    const [loading, setLoading] = useState(true);
    const [busy, setBusy] = useState(false);
    const [error, setError] = useState<string | null>(null);
    const [notice, setNotice] = useState<string | null>(null);
    const [band, setBand] = useState('');
    const [window_, setWindow] = useState('');
    const [h1, setH1] = useState('');
    const [h1Kg, setH1Kg] = useState('');
    const [loris, setLoris] = useState('');
    const [lorisFine, setLorisFine] = useState('');
    const [stockSi, setStockSi] = useState('');
    const [stockSiFine, setStockSiFine] = useState('');
    const [stockBg, setStockBg] = useState('');
    const [stockBgFine, setStockBgFine] = useState('');
    const [ggSloOpen, setGgSloOpen] = useState('');
    const [ggSloOpenFine, setGgSloOpenFine] = useState('');
    const [gtmOpen, setGtmOpen] = useState('');
    const [gtmOpenFine, setGtmOpenFine] = useState('');
    const [country, setCountry] = useState<Country>('all');
    const [openTrend, setOpenTrend] = useState<string | null>(null);
    const [trendDays, setTrendDays] = useState<30 | 365>(30);
    const [trendPoints, setTrendPoints] = useState<HistoryPoint[] | null>(null);
    const [trendLoading, setTrendLoading] = useState(false);
    const [openDetail, setOpenDetail] = useState<string | null>(null);
    const [detail, setDetail] = useState<Detail | null>(null);
    const [detailLoading, setDetailLoading] = useState(false);
    const [detailError, setDetailError] = useState<string | null>(null);
    const [openWarehouses, setOpenWarehouses] = useState<Record<string, boolean>>({});
    const detailKeyRef = useRef<string | null>(null);

    const load = useCallback(async () => {
        setLoading(true);
        setError(null);
        try {
            const d = await api<Overview>('/gold-coverage-v2/overview-v3', token);
            setData(d);
            setBand(String(d.settings.band_pct));
            setWindow(String(d.settings.window_days));
            setH1(String(d.settings.h1_manual_eur));
            setH1Kg(String(d.settings.h1_manual_kg ?? 0));
            setLoris(String(d.settings.loris_manual_eur ?? 0));
            setLorisFine(String(d.settings.loris_manual_fine_g ?? 0));
            setStockSi(String(d.settings.stock_si_manual_eur ?? 0));
            setStockSiFine(String(d.settings.stock_si_manual_fine_g ?? 0));
            setStockBg(String(d.settings.stock_bg_manual_eur ?? 0));
            setStockBgFine(String(d.settings.stock_bg_manual_fine_g ?? 0));
            setGgSloOpen(String(d.settings.gg_slo_open_eur ?? 0));
            setGgSloOpenFine(String(d.settings.gg_slo_open_fine_g ?? 0));
            setGtmOpen(String(d.settings.gtm_open_eur ?? 0));
            setGtmOpenFine(String(d.settings.gtm_open_fine_g ?? 0));
        } catch (e) {
            setError(e instanceof Error ? e.message : String(e));
        } finally {
            setLoading(false);
        }
    }, [token]);

    useEffect(() => {
        void load();
    }, [load]);

    const saveSettings = async () => {
        setBusy(true);
        setError(null);
        setNotice(null);
        try {
            await mutate('/gold-coverage-v2/settings', token, 'PUT', {
                band_pct: decimalInput(band),
                window_days: decimalInput(window_),
                h1_manual_eur: decimalInput(h1),
                h1_manual_kg: decimalInput(h1Kg),
                loris_manual_eur: decimalInput(loris),
                loris_manual_fine_g: decimalInput(lorisFine),
                stock_si_manual_eur: decimalInput(stockSi),
                stock_si_manual_fine_g: decimalInput(stockSiFine),
                stock_bg_manual_eur: decimalInput(stockBg),
                stock_bg_manual_fine_g: decimalInput(stockBgFine),
                gg_slo_open_eur: decimalInput(ggSloOpen),
                gg_slo_open_fine_g: decimalInput(ggSloOpenFine),
                gtm_open_eur: decimalInput(gtmOpen),
                gtm_open_fine_g: decimalInput(gtmOpenFine),
            });
            setNotice(t.saved);
            await load();
        } catch (e) {
            setError(e instanceof Error ? e.message : String(e));
        } finally {
            setBusy(false);
        }
    };

    const loadTrend = useCallback(
        async (metric: string, days: 30 | 365) => {
            setTrendLoading(true);
            setTrendPoints(null);
            try {
                const to = localIsoDate();
                const from = localIsoDate(new Date(Date.now() - days * 86400000));
                const res = await api<{ points: HistoryPoint[] }>(
                    `/gold-coverage/history?scope=40.63&metric=${encodeURIComponent(metric)}&from=${from}&to=${to}`,
                    token
                );
                setTrendPoints(res.points);
            } catch (e) {
                setError(e instanceof Error ? e.message : String(e));
            } finally {
                setTrendLoading(false);
            }
        },
        [token]
    );

    const toggleTrend = (key: string) => {
        if (openTrend === key) {
            setOpenTrend(null);
            setTrendPoints(null);
            return;
        }
        setOpenTrend(key);
        void loadTrend(key, trendDays);
    };

    const changeTrendDays = (days: 30 | 365) => {
        setTrendDays(days);
        if (openTrend) void loadTrend(openTrend, days);
    };

    const toggleDetail = async (key: string) => {
        if (openDetail === key) {
            detailKeyRef.current = null;
            setOpenDetail(null);
            setDetail(null);
            return;
        }
        detailKeyRef.current = key;
        setOpenDetail(key);
        setDetail(null);
        if (HEDGE_METRICS.includes(key)) return;
        setDetailError(null);
        setOpenWarehouses({});
        setDetailLoading(true);
        try {
            const d = await api<Detail>(`/gold-coverage-v2/detail?v=3&metric=${encodeURIComponent(key)}`, token);
            // Odgovor za ladicu koja je u medjuvremenu zatvorena ili zamijenjena se odbacuje.
            if (detailKeyRef.current === key) setDetail(d);
        } catch (e) {
            if (detailKeyRef.current === key) setDetailError(e instanceof Error ? e.message : String(e));
        } finally {
            if (detailKeyRef.current === key) setDetailLoading(false);
        }
    };

    const toggleWarehouse = (code: string) => setOpenWarehouses((prev) => ({ ...prev, [code]: !prev[code] }));

    const groups = useMemo(() => {
        const out: Record<string, Row[]> = Object.fromEntries(SECTIONS.map((x) => [x.key, [] as Row[]]));
        (data?.rows ?? []).forEach((r) => out[r.group]?.push(r));
        return out;
    }, [data]);

    const rowByKey = useCallback((key: string) => data?.rows.find((r) => r.key === key), [data]);

    const formatFine = (grams: number) => {
        if (Math.abs(grams) >= 1000) return `${formatNumber(grams / 1000, locale)} kg`;
        return `${formatNumber(grams, locale)} g`;
    };

    if (loading) return <BrandSpinner />;
    if (error && !data) return <p className="error">{error}</p>;
    if (!data) return null;

    const signalText = (s: Signal) => {
        const dir = s.direction === 'shortage' ? t.shortage : s.direction === 'surplus' ? t.surplus : t.flat;
        const state = s.status === 'green' ? t.green : s.status === 'red' ? t.red : t.unknown;
        const pct = s.deviation_pct === null ? '-' : `${formatNumber(s.deviation_pct, locale)} %`;
        return `${state} (${pct} / ${formatNumber(s.band_pct, locale)} %), ${dir}`;
    };

    const th: React.CSSProperties = { padding: '6px 10px' };
    const thNum: React.CSSProperties = { padding: '6px 10px', textAlign: 'right' };
    const td: React.CSSProperties = { padding: '6px 10px' };
    const tdNum: React.CSSProperties = { padding: '6px 10px', textAlign: 'right', fontFamily: 'monospace' };
    const money = (v: number | null | undefined) => (v === null || v === undefined ? '-' : formatMoney(v, locale));

    const articleTable = (articles: DetailArticle[], withPhysical = false) => {
        if (articles.length === 0) return <p className="muted">{t.noArticles}</p>;
        const sorted = [...articles].sort((a, b) => b.book_eur - a.book_eur);
        return (
            <div style={{ maxHeight: '400px', overflowY: 'auto' }}>
                <table className="admin-table" style={{ width: '100%', fontSize: '0.85rem' }}>
                    <thead>
                        <tr style={{ background: '#f1f5f9' }}>
                            <th style={th}>{t.sku}</th>
                            <th style={th}>{t.articleName}</th>
                            <th style={thNum}>{t.qty}</th>
                            <th style={thNum}>{t.fine}</th>
                            <th style={thNum}>{t.book}</th>
                            <th style={thNum}>{t.spot}</th>
                            {withPhysical ? <th style={thNum}>{t.physical}</th> : null}
                        </tr>
                    </thead>
                    <tbody>
                        {sorted.map((a) => (
                            <tr key={`${a.metal}-${a.sku}-${a.name ?? ''}`} style={{ borderBottom: '1px solid #e2e8f0' }}>
                                <td style={td}>
                                    <code>{a.sku}</code>
                                    {a.metal === 'Ag' ? <span className="muted"> (Ag)</span> : null}
                                </td>
                                <td style={td}>{a.name || '-'}</td>
                                <td style={tdNum}>{formatNumber(a.qty, locale)}</td>
                                <td style={tdNum}>{formatFine(a.fine_g)}</td>
                                <td style={tdNum}>{money(a.book_eur)}</td>
                                <td style={tdNum}>{money(a.spot_eur)}</td>
                                {withPhysical ? <td style={tdNum}>{a.physical_qty !== undefined ? formatNumber(a.physical_qty, locale) : '-'}</td> : null}
                            </tr>
                        ))}
                    </tbody>
                </table>
            </div>
        );
    };

    const silverBlock = (articles: DetailArticle[] | undefined) =>
        articles && articles.length > 0 ? (
            <div style={{ marginTop: '0.75rem' }}>
                <strong style={{ fontSize: '0.85rem' }}>{t.silverApart}</strong>
                {articleTable(articles)}
            </div>
        ) : null;

    const partsTable = (parts: DetailPart[], title: string) =>
        parts.length === 0 ? null : (
            <div style={{ marginBottom: '1rem' }}>
                <strong style={{ fontSize: '0.85rem' }}>{title}</strong>
                <table className="admin-table" style={{ width: '100%', fontSize: '0.85rem' }}>
                    <thead>
                        <tr style={{ background: '#f1f5f9' }}>
                            <th style={th}>{t.item}</th>
                            <th style={thNum}>{t.qty}</th>
                            <th style={thNum}>{t.fine}</th>
                            <th style={thNum}>{t.book}</th>
                            <th style={thNum}>{t.spot}</th>
                        </tr>
                    </thead>
                    <tbody>
                        {parts.map((p, idx) => (
                            <tr key={`${p.key ?? p.label}-${idx}`} style={{ borderBottom: '1px solid #e2e8f0' }}>
                                <td style={td}>{p.label}{p.set_at ? <span style={{ color: '#64748b', fontSize: '0.78rem' }}>{` (${p.set_at.slice(0, 10)}${p.set_by ? `, ${p.set_by}` : ''})`}</span> : null}</td>
                                <td style={tdNum}>{p.qty ? formatNumber(p.qty, locale) : '-'}</td>
                                <td style={tdNum}>{formatFine(p.fine_g)}</td>
                                <td style={tdNum}>{money(p.book_eur)}</td>
                                <td style={tdNum}>{money(p.spot_eur)}</td>
                            </tr>
                        ))}
                    </tbody>
                </table>
            </div>
        );

    const qtyPairs = (pairs: Array<{ label: string; qty: number }>, title: string) =>
        pairs.length === 0 ? null : (
            <div style={{ marginBottom: '1rem' }}>
                <strong style={{ fontSize: '0.85rem' }}>{title}</strong>
                <table className="admin-table" style={{ width: '100%', fontSize: '0.85rem' }}>
                    <tbody>
                        {pairs.map((p) => (
                            <tr key={p.label} style={{ borderBottom: '1px solid #e2e8f0' }}>
                                <td style={td}>{p.label}</td>
                                <td style={tdNum}>{formatNumber(p.qty, locale)}</td>
                            </tr>
                        ))}
                    </tbody>
                </table>
            </div>
        );

    const warehouseTable = (d: Detail) => {
        if (d.warehouses.length === 0) return null;
        const split = ['s2', 's2b', 'l1_stanje', 'l2_poslovnice', 'l3_skladiste'].includes(d.metric);
        const cols = split ? 10 : 5;
        return (
            <div style={{ marginBottom: '1rem' }}>
                <table className="admin-table" style={{ width: '100%', fontSize: '0.85rem' }}>
                    <thead>
                        <tr style={{ background: '#f1f5f9' }}>
                            <th style={th}>{t.warehouse}</th>
                            <th style={thNum}>{t.qty}</th>
                            <th style={thNum}>{t.fine}</th>
                            <th style={thNum}>{t.book}</th>
                            <th style={thNum}>{t.spot}</th>
                            {split ? (
                                <>
                                    <th style={thNum}>{t.auQty}</th>
                                    <th style={thNum}>{t.auFine}</th>
                                    <th style={thNum}>{t.agQty}</th>
                                    <th style={thNum}>{t.agFine}</th>
                                    <th style={th} />
                                </>
                            ) : null}
                        </tr>
                    </thead>
                    <tbody>
                        {d.warehouses.flatMap((w) => {
                            const open = !!openWarehouses[w.warehouse_code];
                            const line = (
                                <tr
                                    key={w.warehouse_code}
                                    style={{ borderBottom: '1px solid #e2e8f0', cursor: split ? 'pointer' : undefined }}
                                    onClick={split ? () => toggleWarehouse(w.warehouse_code) : undefined}
                                >
                                    <td style={td}>
                                        {w.warehouse_code} {w.warehouse_name}
                                        {w.delivery_only ? <span className="muted"> ({t.deliveryOnly})</span> : null}
                                    </td>
                                    <td style={tdNum}>{formatNumber(w.qty, locale)}</td>
                                    <td style={tdNum}>{formatFine(w.fine_g)}</td>
                                    <td style={tdNum}>{money(w.book_eur)}</td>
                                    <td style={tdNum}>{money(w.spot_eur)}</td>
                                    {split ? (
                                        <>
                                            <td style={tdNum}>{formatNumber(w.physical_qty ?? 0, locale)}</td>
                                            <td style={tdNum}>{formatFine(w.au_fine_g ?? 0)}</td>
                                            <td style={tdNum}>{w.ag_qty ? formatNumber(w.ag_qty, locale) : '-'}</td>
                                            <td style={tdNum}>{w.ag_qty ? formatFine(w.ag_fine_g ?? 0) : '-'}</td>
                                            <td style={{ ...td, textAlign: 'center' }}>
                                                <button type="button" className="link-btn" onClick={(e) => { e.stopPropagation(); toggleWarehouse(w.warehouse_code); }}>
                                                    {open ? t.close : t.expand}
                                                </button>
                                            </td>
                                        </>
                                    ) : null}
                                </tr>
                            );
                            if (!split || !open) return [line];
                            return [
                                line,
                                <tr key={`${w.warehouse_code}-articles`}>
                                    <td colSpan={cols} style={{ padding: '0.5rem 0.75rem 0.75rem 1.5rem', background: '#ffffff' }}>
                                        {articleTable(w.articles ?? [], true)}
                                        {silverBlock(w.silver_articles)}
                                    </td>
                                </tr>,
                            ];
                        })}
                    </tbody>
                </table>
            </div>
        );
    };

    const detailInfo = (d: Detail) => {
        const i = d.info;
        const lines: string[] = [];
        if (i.gross) {
            lines.push(
                `${t.gross}: ${formatMoney(i.gross.book_eur, locale)} / ${formatFine(i.gross.fine_g)} / ${formatMoney(i.gross.spot_eur, locale)} ${t.spotLabel.toLowerCase()}; ${t.writeoffs}: ${formatFine(i.writeoff_fine_g ?? 0)} / ${formatMoney(i.writeoff_spot_eur ?? 0, locale)}`
            );
        }
        if (i.order_count !== undefined) {
            lines.push(`${t.orders}: ${i.order_count} · ${t.unpaid}: ${formatMoney(i.remaining_eur ?? 0, locale)}`);
        }
        if (i.items_book_eur !== undefined) {
            const hr = locale === 'hr';
            lines.push(
                `${hr ? 'Zbroj zlatnih stavki' : 'Sum of gold items'}: ${formatMoney(i.items_book_eur, locale)} · ${hr ? 'razlika zaglavlje - stavke' : 'header minus items'}: ${formatMoney(i.header_minus_items_eur ?? 0, locale)}`
            );
            if (i.savings?.book_eur) {
                lines.push(`${hr ? 'Zlatna štednja (u I1, bez grama)' : 'Gold savings (in I1, no grams)'}: ${formatMoney(i.savings.book_eur, locale)}`);
            }
            if (i.packaging?.book_eur) {
                lines.push(`${hr ? 'Ambalaža izvan I1' : 'Packaging outside I1'}: ${formatMoney(i.packaging.book_eur, locale)}`);
            }
        }
        if (i.manual_unattributed_book_eur) {
            lines.push(`${t.unattributed}: ${formatMoney(i.manual_unattributed_book_eur, locale)} / ${formatFine(i.manual_unattributed_fine_g ?? 0)}`);
        }
        if (i.rows_priced_by_spot) {
            lines.push(`${t.pricedBySpot}: ${i.rows_priced_by_spot}`);
        }
        if (i.silver && i.silver.qty > 0) {
            lines.push(`${t.silverApart}: ${formatNumber(i.silver.qty, locale)} ${t.qty.toLowerCase()} / ${formatFine(i.silver.fine_g)}`);
        }
        if (lines.length === 0) return null;
        return (
            <div style={{ marginBottom: '1rem' }}>
                {lines.map((l) => (
                    <p key={l} className="muted" style={{ margin: '0.2rem 0', fontSize: '0.8rem' }}>
                        {l}
                    </p>
                ))}
            </div>
        );
    };

    const renderDetail = (r: Row) => {
        const d = detail && detail.metric === r.key ? detail : null;
        const empty =
            d &&
            d.warehouses.length === 0 &&
            d.articles.length === 0 &&
            d.silver_articles.length === 0 &&
            d.parts.length === 0 &&
            d.by_source.length === 0 &&
            d.by_layer.length === 0;
        return (
            <div style={{ margin: '0.5rem 0 1rem', padding: '1.25rem', background: '#f8fafc', border: '1px solid #e2e8f0', borderRadius: '8px' }}>
                <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', marginBottom: '1rem' }}>
                    <h4 style={{ margin: 0 }}>
                        {t.detailTitle}: {r.label}
                    </h4>
                    <button type="button" className="btn btn-sm btn-secondary" onClick={() => void toggleDetail(r.key)}>
                        ✕ {t.close}
                    </button>
                </div>
                {HEDGE_METRICS.includes(r.key) ? <HedgeTradesPanel locale={locale} token={token} canEdit={!!data?.can_edit} onChanged={() => void load()} /> : null}
                {detailLoading ? <p className="muted">{t.trendLoading}</p> : null}
                {detailError ? <p className="error">{detailError}</p> : null}
                {d ? (
                    <>
                        {detailInfo(d)}
                        {partsTable(d.parts, t.parts)}
                        {qtyPairs(d.by_source, t.bySource)}
                        {qtyPairs(d.by_layer, t.byLayer)}
                        {warehouseTable(d)}
                        {d.articles.length > 0 ? articleTable(d.articles) : null}
                        {silverBlock(d.silver_articles)}
                        {empty && !d.info.gross && d.info.order_count === undefined ? <p className="muted">{t.noDetail}</p> : null}
                    </>
                ) : null}
            </div>
        );
    };

    const renderRows = (rows: Row[]) =>
        rows.flatMap((r) => {
            const isTotal = TOTAL_ROWS.includes(r.key);
            const line = (
                <tr
                    key={r.key}
                    className={isTotal ? 'is-total' : undefined}
                    title={r.note ?? undefined}
                    style={isTotal ? { fontWeight: 600, borderTop: '2px solid #cbd5e1', background: '#f8fafc' } : undefined}
                >
                    <td>
                        {DETAIL_METRICS.includes(r.key) || HEDGE_METRICS.includes(r.key) ? (
                            <span role="button" tabIndex={0} style={{ cursor: 'pointer' }} onClick={() => void toggleDetail(r.key)} onKeyDown={(e) => { if (e.key === 'Enter') void toggleDetail(r.key); }}>
                                {r.label}
                            </span>
                        ) : (
                            r.label
                        )}
                    </td>
                    <td className="num">{r.qty ? formatNumber(r.qty, locale) : '-'}</td>
                    <td className="num">{formatFine(r.fine_g)}</td>
                    <td className="num">{formatMoney(r.book_eur, locale)}</td>
                    <td className="num">{formatMoney(r.spot_eur, locale)}</td>
                    <td className="num" style={{ whiteSpace: 'nowrap' }}>
                        {DETAIL_METRICS.includes(r.key) || HEDGE_METRICS.includes(r.key) ? (
                            <button
                                type="button"
                                className="btn btn-sm btn-secondary"
                                onClick={() => void toggleDetail(r.key)}
                                style={{ fontSize: '0.75rem', padding: '2px 8px', marginRight: '0.5rem' }}
                            >
                                {openDetail === r.key ? t.close : t.view}
                            </button>
                        ) : null}
                        <button type="button" className="link-btn" onClick={() => toggleTrend(r.key)}>
                            {t.trend}
                        </button>
                    </td>
                </tr>
            );
            const out: React.ReactNode[] = [line];
            if (openDetail === r.key) {
                out.push(
                    <tr key={`${r.key}-detail`}>
                        <td colSpan={6} style={{ padding: 0 }}>
                            {renderDetail(r)}
                        </td>
                    </tr>
                );
            }
            if (openTrend !== r.key) return out;
            return [
                ...out,
                <tr key={`${r.key}-trend`}>
                    <td colSpan={6} className="coverage-trend-cell">
                        <div className="filters flat">
                            <button type="button" onClick={() => changeTrendDays(30)} disabled={trendDays === 30}>
                                {t.trend30}
                            </button>
                            <button type="button" onClick={() => changeTrendDays(365)} disabled={trendDays === 365}>
                                {t.trend365}
                            </button>
                            <button type="button" onClick={() => toggleTrend(r.key)}>
                                {t.trendClose}
                            </button>
                        </div>
                        {trendLoading ? (
                            <p className="muted">{t.trendLoading}</p>
                        ) : trendPoints && trendPoints.length >= 2 ? (
                            <>
                                <Sparkline points={trendPoints} />
                                {definitionBreaks(trendPoints).length > 0 ? (
                                    <p className="muted" style={{ margin: '0.25rem 0 0' }}>
                                        {locale === 'en' ? 'Definition change (not a business change) on: ' : 'Promjena definicije izračuna (nije promjena poslovanja) na dan: '}
                                        {definitionBreaks(trendPoints).join(', ')}
                                    </p>
                                ) : null}
                            </>
                        ) : (
                            <p className="muted">{t.trendEmpty}</p>
                        )}
                    </td>
                </tr>,
            ];
        });

    const rec = data.transit_reconcile;
    const z1 = rowByKey('z1');
    const z1b = rowByKey('z1b');
    const z1v2 = rowByKey('z1_v2');
    const i1 = rowByKey('i1');
    const visibleSections = SECTIONS.filter((x) => country === 'all' || x.countries.includes(country));
    const sectionRows = (key: string) => {
        const rows = groups[key] ?? [];
        const only = key === 'result' ? RESULT_BY_COUNTRY[country] : undefined;
        return only ? rows.filter((r) => only.includes(r.key)) : rows;
    };
    const raspolozivoSpotEur =
        (rowByKey('s1')?.spot_eur ?? 0) +
        (rowByKey('s2')?.spot_eur ?? 0) +
        (rowByKey('s2b')?.spot_eur ?? 0) +
        (rowByKey('s3')?.all_transit?.spot_eur ?? rowByKey('s3')?.spot_eur ?? 0) +
        (rowByKey('s4')?.spot_eur ?? 0);
    const z1Color = data.signal.status === 'green' ? '#16a34a' : data.signal.status === 'red' ? '#dc2626' : '#64748b';
    const z1Bg = data.signal.status === 'green' ? '#f0fdf4' : data.signal.status === 'red' ? '#fef2f2' : '#f8fafc';
    const z1bPositive = (z1b?.spot_eur ?? 0) >= 0;


    const setHead: React.CSSProperties = { margin: '0 0 0.5rem', fontSize: '0.85rem', fontWeight: 600, color: '#334155', textTransform: 'uppercase', letterSpacing: '0.03em' };
    const setLabel: React.CSSProperties = { display: 'flex', flexDirection: 'column', gap: '0.3rem', fontSize: '0.85rem', color: '#334155' };
    const setInput: React.CSSProperties = { width: '9rem', padding: '0.4rem 0.55rem', border: '1px solid #94a3b8', borderRadius: 4, background: '#fff', textAlign: 'right', fontSize: '0.9rem' };
    const manualRows = [
        { key: 'h1', label: t.rowH1, eur: h1, setEur: setH1, qty: h1Kg, setQty: setH1Kg, unit: t.unitKg },
        { key: 'h2', label: t.rowH2, eur: loris, setEur: setLoris, qty: lorisFine, setQty: setLorisFine, unit: t.unitG },
        { key: 'h3', label: t.rowH3, eur: stockSi, setEur: setStockSi, qty: stockSiFine, setQty: setStockSiFine, unit: t.unitG },
        { key: 'h4', label: t.rowH4, eur: stockBg, setEur: setStockBg, qty: stockBgFine, setQty: setStockBgFine, unit: t.unitG },
        { key: 'ggslo', label: t.rowGgSlo, eur: ggSloOpen, setEur: setGgSloOpen, qty: ggSloOpenFine, setQty: setGgSloOpenFine, unit: t.unitG },
        { key: 'gtm', label: t.rowGtm, eur: gtmOpen, setEur: setGtmOpen, qty: gtmOpenFine, setQty: setGtmOpenFine, unit: t.unitG },
    ];
    return (
        <div className="card admin-block coverage-v2-module">
            <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'flex-start', flexWrap: 'wrap', gap: '1rem' }}>
                <div>
                    <Breadcrumb
                        items={[
                            { label: locale === 'hr' ? 'Skladiste' : 'Warehouse', onClick: onBack },
                            { label: `40.63 ${t.title}` },
                        ]}
                    />
                    <div className="bi-module-hero" style={{ marginTop: '0.5rem' }}>
                        <div className="bi-module-hero-icon">⚖️</div>
                        <div className="bi-module-hero-text">
                            <h1>40.63 · {t.title}</h1>
                            <p className="muted">{t.lead}</p>
                        </div>
                    </div>
                </div>
                <button type="button" className="btn btn-secondary" onClick={() => void load()} disabled={loading}>
                    🔄 {locale === 'hr' ? 'Osvježi' : 'Refresh'}
                </button>
            </div>

            <div
                style={{
                    background: '#f8fafc',
                    border: '1px solid #e2e8f0',
                    borderRadius: '8px',
                    padding: '0.75rem 1.25rem',
                    margin: '1.25rem 0',
                    display: 'flex',
                    flexWrap: 'wrap',
                    justifyContent: 'space-between',
                    alignItems: 'center',
                    gap: '1rem',
                    fontSize: '0.875rem',
                }}
            >
                <div style={{ display: 'flex', alignItems: 'center', gap: '1.5rem', flexWrap: 'wrap' }}>
                    <div>
                        <span className="muted">{t.spotLabel}: </span>
                        <strong>{formatMoney(data.spot.eur_per_gram, locale)}/g</strong>
                    </div>
                    <div>
                        <span className="muted">{t.asOf}: </span>
                        <span>{data.freshness.stock_as_of ?? '-'}</span>
                        {data.freshness.stock_age_days !== null ? <span className="muted"> ({data.freshness.stock_age_days} d)</span> : null}
                    </div>
                    <div>
                        <span className="muted">{t.openOrders}: </span>
                        <span>{data.freshness.open_orders_as_of ?? '-'}</span>
                        {data.freshness.open_orders_age_hours != null ? <span className="muted"> ({data.freshness.open_orders_age_hours} h)</span> : null}
                    </div>
                </div>
            </div>
            {data.freshness.unknown_warehouses?.length ? (
                <p className="card-alert card-alert-danger error" style={{ padding: '0.5rem 0 0.5rem 1rem' }}>
                    {locale === 'en'
                        ? 'Warehouses in the stock snapshot without a row in admin 128, counted as branches: '
                        : 'Skladista u snimci zalihe bez retka u adminu 128, racunaju se kao poslovnica: '}
                    {data.freshness.unknown_warehouses.map((w) => `${w.code} ${w.name}`).join(', ')}
                </p>
            ) : null}
            <UnknownWarehousesPopup
                token={token}
                locale={locale}
                unknown={data.freshness.unknown_warehouses ?? []}
                canEdit={!!data.can_edit}
                onDone={() => void load()}
            />
            {data.freshness.stale ? <p className="card-alert card-alert-danger error" style={{ padding: '0.5rem 0 0.5rem 1rem' }}>{t.stale}</p> : null}
            {error ? <p className="error">{error}</p> : null}
            {notice ? <p className="muted">{notice}</p> : null}

            {country === 'SI' || country === 'BG' ? null : (
            <div style={{ display: 'grid', gridTemplateColumns: 'repeat(auto-fit, minmax(220px, 1fr))', gap: '1rem', marginBottom: '1.5rem' }}>
                <div className="card" style={{ padding: '1.25rem', borderLeft: `4px solid ${z1Color}`, background: z1Bg }}>
                    <p className="eyebrow" style={{ margin: 0, color: '#64748b' }}>
                        Z1
                    </p>
                    <p className="muted" style={{ margin: '0.15rem 0 0', fontSize: '0.75rem' }}>{z1?.label.split(' = ')[1] ? `= ${z1.label.split(' = ')[1]}` : null}</p>
                    <h2 style={{ margin: '0.4rem 0 0.2rem', color: z1Color, fontSize: '1.75rem' }}>
                        {formatMoney(z1?.spot_eur ?? 0, locale)}
                    </h2>
                    <p style={{ margin: 0, fontSize: '0.85rem', color: '#475569' }}>{signalText(data.signal)}</p>
                </div>

                <div
                    className="card"
                    style={{ padding: '1.25rem', borderLeft: `4px solid ${z1bPositive ? '#16a34a' : '#dc2626'}`, background: z1bPositive ? '#f0fdf4' : '#fef2f2' }}
                >
                    <p className="eyebrow" style={{ margin: 0, color: '#64748b' }}>
                        Z1b
                    </p>
                    <p className="muted" style={{ margin: '0.15rem 0 0', fontSize: '0.75rem' }}>{z1b?.label.split(' = ')[1] ? `= ${z1b.label.split(' = ')[1]}` : null}</p>
                    <h2 style={{ margin: '0.4rem 0 0.2rem', color: z1bPositive ? '#15803d' : '#b91c1c', fontSize: '1.75rem' }}>
                        {formatMoney(z1b?.spot_eur ?? 0, locale)}
                    </h2>
                    <p style={{ margin: 0, fontSize: '0.85rem', color: '#475569' }}>
                        {locale === 'hr' ? 'Bez H1-H4 (StoneX, Loris, SLO, BG)' : 'Without H1-H4 (StoneX, Loris, SLO, BG)'}
                    </p>
                </div>

                <div className="card" style={{ padding: '1.25rem', borderLeft: '4px solid #475569', background: '#f8fafc' }}>
                    <p className="eyebrow" style={{ margin: 0, color: '#64748b' }}>
                        Z1-V2
                    </p>
                    <p className="muted" style={{ margin: '0.15rem 0 0', fontSize: '0.75rem' }}>{z1v2?.label.split(' = ')[1] ? `= ${z1v2.label.split(' = ')[1]}` : null}</p>
                    <h2 style={{ margin: '0.4rem 0 0.2rem', color: '#334155', fontSize: '1.75rem' }}>
                        {formatMoney(z1v2?.spot_eur ?? 0, locale)}
                    </h2>
                    <p style={{ margin: 0, fontSize: '0.85rem', color: '#475569' }}>
                        {locale === 'hr' ? 'Treca metrika po V2, bez S2b, H1-H4 i robe na putu za lager i partnere' : 'Third metric per V2, without S2b, H1-H4 and transit for lager and partners'}
                    </p>
                </div>

                <div className="card" style={{ padding: '1.25rem', borderLeft: '4px solid #d97706', background: '#fffbeb' }}>
                    <p className="eyebrow" style={{ margin: 0, color: '#64748b' }}>
                        {locale === 'hr' ? 'I1 NEISPORUČENE NARUDŽBE' : 'I1 UNDELIVERED ORDERS'}
                    </p>
                    <h2 style={{ margin: '0.4rem 0 0.2rem', color: '#b45309', fontSize: '1.75rem' }}>
                        {formatMoney(i1?.book_eur ?? 0, locale)}
                    </h2>
                    <p style={{ margin: 0, fontSize: '0.85rem', color: '#475569' }}>
                        {i1?.order_count ?? 0} {locale === 'hr' ? 'nar.' : 'ord.'} ·{' '}
                        {formatMoney(i1?.remaining_eur ?? 0, locale)} {locale === 'hr' ? 'nenaplaćeno' : 'unpaid'}
                    </p>
                    {i1?.savings?.book_eur ? (
                        <p style={{ margin: 0, fontSize: '0.85rem', color: '#475569' }}>
                            {locale === 'hr' ? 'od toga Zlatna štednja (bez grama)' : 'of which Gold savings (no grams)'}: {formatMoney(i1.savings.book_eur, locale)}
                        </p>
                    ) : null}
                    {i1?.packaging?.book_eur ? (
                        <p className="muted" style={{ margin: 0, fontSize: '0.85rem' }}>
                            {locale === 'hr' ? 'ambalaža izvan I1' : 'packaging outside I1'}: {formatMoney(i1.packaging.book_eur, locale)}
                        </p>
                    ) : null}
                </div>

                <div className="card" style={{ padding: '1.25rem', borderLeft: '4px solid #2563eb', background: '#eff6ff' }}>
                    <p className="eyebrow" style={{ margin: 0, color: '#64748b' }}>
                        {locale === 'hr' ? 'RASPOLOŽIVO S1+S2+S2b+S3 ukupno+S4' : 'AVAILABLE S1+S2+S2b+S3 total+S4'}
                    </p>
                    <h2 style={{ margin: '0.4rem 0 0.2rem', color: '#1d4ed8', fontSize: '1.75rem' }}>
                        {formatMoney(raspolozivoSpotEur, locale)}
                    </h2>
                    <p style={{ margin: 0, fontSize: '0.85rem', color: '#475569' }}>{locale === 'hr' ? 'Spot EUR' : 'Spot EUR'}</p>
                </div>
            </div>

            )}
            <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', flexWrap: 'wrap', gap: '0.75rem', margin: '0 0 0.75rem' }}>
                <h3 style={{ margin: 0 }}>{locale === 'hr' ? 'Glavna tablica' : 'Main table'}</h3>
                <label style={{ display: 'flex', alignItems: 'center', gap: '0.5rem', fontSize: '0.875rem' }}>
                    {t.country}
                    <select value={country} onChange={(e) => setCountry(e.target.value as Country)}>
                        {(['all', 'HR', 'SI', 'BG'] as Country[]).map((c) => (
                            <option key={c} value={c}>
                                {t.countries[c]}
                            </option>
                        ))}
                    </select>
                </label>
            </div>
            {country === 'SI' || country === 'BG' ? <p className="muted" style={{ margin: '0 0 0.5rem' }}>{t.countryNote}</p> : null}
            <table className="data-table">
                <thead>
                    <tr>
                        <th>{t.item}</th>
                        <th className="num">{t.qty}</th>
                        <th className="num">{t.fine}</th>
                        <th className="num">{t.book}</th>
                        <th className="num">{t.spot}</th>
                        <th className="num">{t.trend}</th>
                    </tr>
                </thead>
                <tbody>
                    {visibleSections.flatMap((sec) => {
                        const rows = sectionRows(sec.key);
                        if (rows.length === 0) return [];
                        return [
                            <tr key={`head-${sec.key}`} className="group-head" style={{ background: '#eef2f7' }}>
                                <th colSpan={6} style={{ textAlign: 'left', padding: '0.5rem 0.75rem', fontSize: '0.8rem', textTransform: 'uppercase', letterSpacing: '0.02em', color: '#475569' }}>
                                    {t.sections[sec.key] ?? sec.key}
                                </th>
                            </tr>,
                            ...renderRows(rows),
                        ];
                    })}
                </tbody>
            </table>

            <p className={data.signal.status === 'red' ? 'error' : 'muted'}>
                Z1: {signalText(data.signal)}
                {data.signal.days !== null ? ` · ${formatNumber(data.signal.days, locale)} ${t.days}` : ''}
            </p>

            <CoverageDayView token={token} locale={locale} scope="40.63" labels={Object.fromEntries(data.rows.map((r) => [r.key, r.label]))} />

            <div className="card" style={{ background: '#f8fafc', border: '1px solid #e2e8f0', padding: '1.25rem', marginTop: '1.5rem' }}>
                <h3 style={{ margin: '0 0 0.75rem' }}>{t.reconcile}</h3>
                <table className="data-table">
                    <tbody>
                        <tr>
                            <td>{t.farosTotal}</td>
                            <td className="num">{rec.faros_qty_in_transit === null ? '-' : formatNumber(rec.faros_qty_in_transit, locale)}</td>
                        </tr>
                        <tr>
                            <td>{t.bisOut}</td>
                            <td className="num">{formatNumber(rec.bis_outbound_qty, locale)}</td>
                        </tr>
                        <tr>
                            <td>{t.bisIn}</td>
                            <td className="num">{formatNumber(rec.bis_inbound_qty, locale)}</td>
                        </tr>
                        <tr>
                            <td>{t.diff}</td>
                            <td className="num">{rec.difference === null ? '-' : formatNumber(rec.difference, locale)}</td>
                        </tr>
                    </tbody>
                </table>
            </div>

            <div className="card" style={{ background: '#f8fafc', border: '1px solid #e2e8f0', padding: '1.25rem', marginTop: '1.25rem' }}>
                <h3 style={{ margin: '0 0 0.75rem' }}>{t.check}</h3>
                {data.double_count_check.ok ? (
                    <p className="card-alert card-alert-info" style={{ margin: '0 0 0.75rem' }}>{t.checkOk}</p>
                ) : (
                    <>
                        <p className="card-alert card-alert-danger error" style={{ margin: '0 0 0.75rem' }}>{t.checkBad}</p>
                        <ul>
                            {data.double_count_check.overflow.map((o) => (
                                <li key={`${o.warehouse_code}-${o.item_code}`}>
                                    {o.warehouse_code} / {o.item_code}: {formatNumber(o.stock_qty, locale)} vs{' '}
                                    {formatNumber(o.reserved_qty + o.committed_qty, locale)}
                                </li>
                            ))}
                        </ul>
                    </>
                )}
                <table className="data-table">
                    <thead>
                        <tr>
                            <th>{locale === 'hr' ? 'Poslovnica' : 'Branch'}</th>
                            <th className="num">{t.physical}</th>
                            <th className="num">{t.reserved}</th>
                            <th className="num">{t.allocated}</th>
                            <th className="num">{t.free}</th>
                        </tr>
                    </thead>
                    <tbody>
                        {data.double_count_check.by_warehouse.map((w) => (
                            <tr key={w.warehouse_code}>
                                <td>
                                    {w.warehouse_code} {w.warehouse_name}
                                </td>
                                <td className="num">{formatNumber(w.physical_qty, locale)}</td>
                                <td className="num">{formatNumber(w.s2_qty, locale)}</td>
                                <td className="num">{formatNumber(w.allocated_qty, locale)}</td>
                                <td className="num">{formatNumber(w.l1_stanje_qty, locale)}</td>
                            </tr>
                        ))}
                    </tbody>
                </table>
            </div>

            {data.can_edit ? (
                <div className="card" style={{ background: '#f8fafc', border: '1px solid #e2e8f0', padding: '1.25rem', marginTop: '1.25rem' }}>
                    <h3 style={{ margin: '0 0 0.75rem' }}>{t.settings}</h3>
                    <h4 style={setHead}>{t.setSignal}</h4>
                    <div style={{ display: 'flex', gap: '1.5rem', flexWrap: 'wrap', marginBottom: '1rem' }}>
                        <label style={setLabel}>
                            {t.band}
                            <input style={setInput} inputMode="decimal" value={band} onChange={(e) => setBand(e.target.value)} disabled={busy} />
                        </label>
                        <label style={setLabel}>
                            {t.window}
                            <input style={setInput} inputMode="numeric" value={window_} onChange={(e) => setWindow(e.target.value)} disabled={busy} />
                        </label>
                    </div>
                    <h4 style={setHead}>{t.setManual}</h4>
                    <table className="data-table" style={{ maxWidth: '52rem', marginBottom: '1rem' }}>
                        <thead>
                            <tr>
                                <th>{t.colItem}</th>
                                <th className="num">{t.colEur}</th>
                                <th className="num">{t.colQty}</th>
                            </tr>
                        </thead>
                        <tbody>
                            {manualRows.map((r) => (
                                <tr key={r.key}>
                                    <td>{r.label}</td>
                                    <td className="num">
                                        <input style={setInput} inputMode="decimal" aria-label={`${r.label} EUR`} value={r.eur} onChange={(e) => r.setEur(e.target.value)} disabled={busy} />
                                    </td>
                                    <td className="num">
                                        <span style={{ display: 'inline-flex', alignItems: 'center', gap: '0.4rem' }}>
                                            <input style={setInput} inputMode="decimal" aria-label={`${r.label} ${r.unit}`} value={r.qty} onChange={(e) => r.setQty(e.target.value)} disabled={busy} />
                                            <span style={{ fontSize: '0.8rem', color: '#64748b', whiteSpace: 'nowrap' }}>{r.unit}</span>
                                        </span>
                                    </td>
                                </tr>
                            ))}
                        </tbody>
                    </table>
                    <button type="button" className="btn btn-primary" style={{ minWidth: '10rem' }} onClick={() => void saveSettings()} disabled={busy}>
                        {t.save}
                    </button>
                    <WarehouseClassifySettings token={token} locale={locale} onChanged={() => void load()} />
                </div>
            ) : null}
        </div>
    );
}
